import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Filter, Trash2, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface HistoryLog {
  id: string;
  event_type: string;
  created_at: string;
  user?: {
    firstname: string;
    lastname: string;
  };
  transaction?: {
    property_number: string;
  };
  event_data: any;
}

export default function HistorySettings() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<HistoryLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventType, setSelectedEventType] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [startDate, endDate]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('transaction_logs')
        .select(`
          *,
          user:users(firstname, lastname),
          transaction:transactions(property_number)
        `)
        .order('created_at', { ascending: false });

      // Appliquer les filtres de date
      if (startDate) {
        query = query.gte('created_at', startDate);
      }
      if (endDate) {
        // Ajouter un jour à la date de fin pour inclure toute la journée
        const endDateTime = new Date(endDate);
        endDateTime.setDate(endDateTime.getDate() + 1);
        query = query.lt('created_at', endDateTime.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      setLogs(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeOldLogs = async () => {
    if (!user?.role === 'admin') return;

    if (!confirm('Êtes-vous sûr de vouloir purger les logs de plus de 3 ans ? Cette action est irréversible.')) {
      return;
    }

    try {
      setPurgeLoading(true);
      
      // Calculer la date limite (3 ans en arrière)
      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

      const { error } = await supabase
        .from('transaction_logs')
        .delete()
        .lt('created_at', threeYearsAgo.toISOString());

      if (error) throw error;

      // Recharger les logs après la purge
      await loadLogs();
      alert('Les anciens logs ont été purgés avec succès');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPurgeLoading(false);
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case 'creation':
        return 'Création';
      case 'modification':
        return 'Modification';
      case 'status_change':
        return 'Changement de statut';
      case 'regul_request':
        return 'Demande de régularisation';
      case 'compromise':
        return 'Passage en compromis';
      case 'acte':
        return 'Passage en acte';
      case 'encaissement':
        return 'Encaissement';
      case 'cloture':
        return 'Clôture';
      case 'deletion':
        return 'Suppression';
      case 'cancellation':
        return 'Annulation';
      default:
        return eventType;
    }
  };

  const getEventDescription = (log: HistoryLog) => {
    const { event_type, event_data } = log;
    
    switch (event_type) {
      case 'creation':
        return `Création de la transaction #${log.transaction?.property_number} - ${event_data.seller} / ${event_data.buyer} - ${formatCurrency(event_data.sale_price)}`;
      
      case 'modification':
        return `Modification de la transaction #${log.transaction?.property_number}${
          event_data.changes ? ` : ${Object.keys(event_data.changes).join(', ')}` : ''
        }`;
      
      case 'status_change':
        return `Statut changé de "${event_data.old_status}" à "${event_data.new_status}"`;
      
      case 'regul_request':
        if (event_data.action === 'approved') {
          return `Régularisation approuvée${event_data.comment ? ` : ${event_data.comment}` : ''}${
            event_data.status_changed ? ' (Transaction repassée en encaissé)' : ''
          }`;
        } else if (event_data.action === 'rejected') {
          return `Régularisation rejetée${event_data.comment ? ` : ${event_data.comment}` : ''}`;
        }
        return `Demande de régularisation : ${event_data.message}`;
      
      case 'compromise':
        return `Passage en compromis (Date : ${formatDate(event_data.compromise_date)})`;
      
      case 'acte':
        return `Passage en acte (Date : ${formatDate(event_data.acte_date)})`;
      
      case 'encaissement':
        return `Encaissement (Date : ${formatDate(event_data.encaissement_date)})`;
      
      case 'cloture':
        return 'Transaction clôturée';

      case 'deletion':
        return `Suppression de la transaction #${event_data.property_number}`;

      case 'cancellation':
        return `Annulation de la transaction (statut précédent : ${event_data.previous_status})`;
      
      default:
        return 'Action non spécifiée';
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'creation':
        return 'bg-green-100 text-green-800';
      case 'modification':
        return 'bg-blue-100 text-blue-800';
      case 'status_change':
        return 'bg-purple-100 text-purple-800';
      case 'regul_request':
        return 'bg-red-100 text-red-800';
      case 'compromise':
        return 'bg-yellow-100 text-yellow-800';
      case 'acte':
        return 'bg-indigo-100 text-indigo-800';
      case 'encaissement':
        return 'bg-teal-100 text-teal-800';
      case 'cloture':
        return 'bg-gray-100 text-gray-800';
      case 'deletion':
        return 'bg-red-100 text-red-800';
      case 'cancellation':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' || 
      log.transaction?.property_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${log.user?.firstname} ${log.user?.lastname}`.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesEventType = selectedEventType === 'all' || log.event_type === selectedEventType;

    return matchesSearch && matchesEventType;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">Historique des actions</h2>
        {user?.role === 'admin' && (
          <button
            onClick={handlePurgeOldLogs}
            disabled={purgeLoading}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
          >
            <Trash2 size={18} />
            <span>{purgeLoading ? 'Purge en cours...' : 'Purger les logs > 3 ans'}</span>
          </button>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {/* Recherche */}
          <div className="col-span-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher par n° de bien ou utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
          </div>

          {/* Filtre par type d'événement */}
          <div>
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="all">Tous les types d'événements</option>
                <option value="creation">Création</option>
                <option value="modification">Modification</option>
                <option value="status_change">Changement de statut</option>
                <option value="regul_request">Régularisation</option>
                <option value="compromise">Compromis</option>
                <option value="acte">Acte</option>
                <option value="encaissement">Encaissement</option>
                <option value="cloture">Clôture</option>
                <option value="deletion">Suppression</option>
                <option value="cancellation">Annulation</option>
              </select>
            </div>
          </div>

          {/* Filtre par date */}
          <div>
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
                <span className="text-gray-500">à</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute top-0 bottom-0 left-6 w-0.5 bg-gray-200" />
            
            <div className="space-y-6">
              {filteredLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  Aucun événement trouvé
                </p>
              ) : (
                filteredLogs.map((log) => (
                  <div key={log.id} className="relative pl-12">
                    <div className="absolute left-4 w-4 h-4 -mt-2 rounded-full border-4 border-white bg-blue-600" />
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventColor(log.event_type)}`}>
                              {getEventTypeLabel(log.event_type)}
                            </span>
                            {log.transaction && (
                              <span className="text-sm font-medium text-gray-600">
                                Transaction #{log.transaction.property_number}
                              </span>
                            )}
                          </div>
                          
                          <p className="mt-2 text-sm text-gray-900">
                            {getEventDescription(log)}
                          </p>

                          {log.event_type === 'modification' && log.event_data.changes && (
                            <div className="mt-2 space-y-1 text-sm">
                              {Object.entries(log.event_data.changes).map(([field, value]: [string, any]) => (
                                <div key={field} className="text-gray-600">
                                  • {field}: {JSON.stringify(value)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm text-gray-900">
                            {log.user?.firstname} {log.user?.lastname}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(log.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}